import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LightIndicator, RestAtShoreButton, GroundingOpener, QuestNarrative,
  ExitRampBanner, PromptCard, ReintegrationCloser, WeatherHint,
} from '@/components/innerworld';
import { useQuest } from '@/hooks/useInnerWorld';

const REALM_NAMES = { 1: 'The Shore', 2: 'The Garden', 3: 'The Hollow', 4: 'The Hearth', 5: 'The Horizon' };

const CompanionSection = ({ companions, companionId, setCompanionId, companionName, setCompanionName }) => (
  <div className="p-5 rounded-2xl mb-6" style={{ background: '#F9F4F0' }} data-testid="companion-section">
    <p className="text-xs tracking-[0.1em] mb-3" style={{ fontFamily: 'Jost, sans-serif', color: '#72826F' }}>
      DID YOU MEET A PART OF YOU HERE? (OPTIONAL)
    </p>
    {companions.length > 0 && (
      <div className="flex flex-wrap gap-2 mb-3">
        {companions.map((c) => (
          <button
            key={c.id}
            onClick={() => { setCompanionId(companionId === c.id ? null : c.id); setCompanionName(''); }}
            className="px-3 py-1.5 rounded-full text-xs transition-all"
            style={{
              background: companionId === c.id ? 'rgba(138,158,143,0.25)' : '#F7F1EE',
              color: companionId === c.id ? '#a9c0ae' : '#5C5968',
              fontFamily: 'Jost, sans-serif',
              border: companionId === c.id ? '1px solid rgba(138,158,143,0.4)' : '1px solid transparent',
            }}
            data-testid={`companion-select-${c.part_name}`}
          >
            {c.part_name}
          </button>
        ))}
      </div>
    )}
    <input
      type="text"
      value={companionName}
      onChange={(e) => { setCompanionName(e.target.value); if (e.target.value) setCompanionId(null); }}
      placeholder="Or give this part a name — e.g. The Watcher"
      maxLength={60}
      className="w-full p-3 rounded-xl text-sm focus:outline-none"
      style={{ background: 'rgba(26,26,46,0.08)', color: '#1A1A2E', fontFamily: 'Jost, sans-serif', border: '1px solid #F0EBE4' }}
      data-testid="companion-name-input"
    />
  </div>
);

export default function InnerWorldQuestPage() {
  const navigate = useNavigate();
  const { realm } = useParams();
  const realmNum = parseInt(realm, 10);
  const { questData, loading, error, submitting, result, submitQuest, toggleKeepsake } = useQuest(realmNum);

  const [response, setResponse] = useState('');
  const [companionId, setCompanionId] = useState(null);
  const [companionName, setCompanionName] = useState('');
  const [keepsakeIncluded, setKeepsakeIncluded] = useState(false);

  const handleToggleKeepsake = async () => {
    if (!result?.entry_id) return;
    const next = !keepsakeIncluded;
    setKeepsakeIncluded(next);
    try {
      await toggleKeepsake(result.entry_id, next);
    } catch {
      setKeepsakeIncluded(!next);
    }
  };

  const handleSubmit = async () => {
    if (!response.trim()) return;
    try {
      const res = await submitQuest({
        realm: realmNum,
        quest_title: questData.quest.quest_title,
        quest_narrative: questData.quest.quest_narrative,
        prompt_text: questData.quest.prompt,
        user_response: response,
        companion_id: companionId,
        companion_name: companionName || null,
      });
      if (res.redirect === 'rest_at_shore') {
        navigate('/inner-world/rest', { state: { crisis: res.show_crisis_resources, lightAwarded: res.light_awarded } });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something quiet went wrong. Your words are safe — try again in a moment.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: '#FBF9F6' }}>
        <div className="w-3 h-3 rounded-full animate-pulse" style={{ background: '#C9A84C', boxShadow: '0 0 20px rgba(201,168,76,0.6)' }} />
        <p className="text-xs tracking-[0.15em]" style={{ fontFamily: 'Jost, sans-serif', color: '#8A8798' }}>THE PATH IS FORMING…</p>
      </div>
    );
  }

  if (error || !questData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 gap-6" style={{ background: '#FBF9F6' }}>
        <p className="text-sm text-center" style={{ fontFamily: 'Jost, sans-serif', color: '#6B6878' }}>{error || 'The path is quiet right now.'}</p>
        <button onClick={() => navigate('/inner-world')} className="px-6 py-2.5 rounded-full text-xs tracking-[0.1em]" style={{ background: 'rgba(201,168,76,0.15)', color: '#C9A84C', fontFamily: 'Jost, sans-serif' }}>
          BACK TO THE MAP
        </button>
      </div>
    );
  }

  if (questData.halted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center" style={{ background: '#FBF9F6' }} data-testid="quest-halted-screen">
        <p className="text-lg leading-relaxed mb-8 max-w-sm" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}>
          {questData.message}
        </p>
        <button onClick={() => navigate('/inner-world/rest')} className="px-8 py-3 rounded-full text-sm tracking-[0.1em]" style={{ background: 'rgba(143,169,190,0.15)', color: '#8fa9be', fontFamily: 'Jost, sans-serif' }} data-testid="halted-rest-button">
          REST AT THE SHORE
        </button>
      </div>
    );
  }

  const { quest, realm_info: realmInfo, disclaimer_text: disclaimer, companions } = questData;
  const submitted = result && result.redirect === 'reintegration_closer';

  return (
    <div className="min-h-screen pb-16" style={{ background: '#FBF9F6' }} data-testid="quest-screen">
      <header className="max-w-md mx-auto px-6 pt-8 pb-2 flex items-center justify-between">
        <RestAtShoreButton />
        <LightIndicator total={result ? result.light_total : questData.light_total || 0} />
      </header>

      <main className="max-w-md mx-auto px-6 pt-6">
        <div className="text-center mb-8">
          <span className="text-xs tracking-[0.2em] block mb-2" style={{ fontFamily: 'Jost, sans-serif', color: realmInfo?.color || '#C9A84C' }}>
            {realmInfo?.name?.toUpperCase()}
          </span>
          <h1 className="text-lg md:text-lg" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }} data-testid="quest-title">
            {quest.quest_title}
          </h1>
          {questData.weather && (
            <div className="mt-2 flex justify-center">
              <WeatherHint tone={questData.weather} color={realmInfo?.color} />
            </div>
          )}
        </div>

        {submitted ? (
          <ReintegrationCloser
            text={quest.reintegration_closer}
            lightAwarded={result.light_awarded}
            newlyUnlocked={result.newly_unlocked}
            realms={Object.fromEntries(Object.entries(REALM_NAMES).map(([k, v]) => [k, { name: v }]))}
            keepsakeIncluded={keepsakeIncluded}
            onToggleKeepsake={handleToggleKeepsake}
            keepsakeCreated={result.keepsake_created}
            onContinue={() => navigate('/inner-world')}
          />
        ) : (
          <>
            <GroundingOpener text={quest.grounding_opener} />
            <QuestNarrative text={quest.quest_narrative} />
            <PromptCard promptText={quest.prompt} value={response} onChange={setResponse} disabled={submitting} />
            {realmNum === 3 && <ExitRampBanner text={quest.exit_ramp || "If this feels like too much right now, you can stop here — tap 'Rest at the Shore' any time. Coming back later is not a failure."} />}
            {realmNum >= 2 && (
              <CompanionSection
                companions={companions || []}
                companionId={companionId}
                setCompanionId={setCompanionId}
                companionName={companionName}
                setCompanionName={setCompanionName}
              />
            )}
            <button
              onClick={handleSubmit}
              disabled={submitting || !response.trim()}
              className="w-full py-4 rounded-full text-sm tracking-[0.12em] transition-all active:scale-[0.98] disabled:opacity-40"
              style={{ background: 'rgba(201,168,76,0.18)', color: '#C9A84C', fontFamily: 'Jost, sans-serif' }}
              data-testid="quest-submit-button"
            >
              {submitting ? 'HOLDING YOUR WORDS…' : 'OFFER THIS REFLECTION'}
            </button>
            {disclaimer && (
              <p className="text-xs leading-relaxed text-center mt-6" style={{ fontFamily: 'Jost, sans-serif', color: '#8A8798' }} data-testid="quest-disclaimer">
                {disclaimer}
              </p>
            )}
          </>
        )}
      </main>
    </div>
  );
}
